(function () {
  'use strict';

  /**
   * Drives the header's language/country flag dropdown
   * (snippets/header-language-selector.liquid). The dropdown always
   * lists the same fixed 5 languages, independent of how many locales
   * are actually configured in Shopify admin.
   *
   * For whichever of the 5 IS a real, available Shopify locale
   * (data-locale-available="true", i.e. present in
   * localization.available_languages), selecting it sets the value of
   * the real, native <select name="locale_code"> inside
   * {% form 'localization' %} and submits that form - Shopify's own
   * native mechanism, so the storefront genuinely changes language. No
   * separate/fake localization system for that case.
   *
   * For any of the 5 NOT yet added as a real Shopify locale, there is no
   * Shopify-side content to switch to (that's a merchant setup step in
   * Settings > Languages, not something this script can fabricate) - so
   * selecting it just updates the displayed flag and remembers the
   * choice, without submitting the form or navigating.
   *
   * Either way, the chosen flag is persisted in localStorage
   * (LANG_STORAGE_KEY) and re-applied on every page load, so the visual
   * selection survives refreshes and navigation regardless of Shopify's
   * own locale state.
   */
  var LANG_STORAGE_KEY = 'headerLangPreference';

  function readStoredLocale() {
    try {
      return window.localStorage.getItem(LANG_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeStoredLocale(code) {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch (e) {
      /* localStorage unavailable (private browsing, disabled, etc.) -
         the flag/dropdown still work for the current page, they just
         won't remember the choice on the next visit. */
    }
  }

  function closeDropdown(root) {
    var trigger = root.querySelector('[data-lang-selector-trigger]');
    var dropdown = root.querySelector('[data-lang-selector-dropdown]');
    if (!dropdown) return;
    dropdown.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function openDropdown(root) {
    var trigger = root.querySelector('[data-lang-selector-trigger]');
    var dropdown = root.querySelector('[data-lang-selector-dropdown]');
    if (!dropdown) return;
    dropdown.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function applyFlag(root, option) {
    var currentFlag = root.querySelector('[data-lang-selector-current-flag]');
    var currentName = root.querySelector('[data-lang-selector-current-name]');
    var flagSrc = option.getAttribute('data-flag-src');
    var langName = option.getAttribute('data-lang-name');
    if (currentFlag) {
      if (flagSrc) currentFlag.setAttribute('src', flagSrc);
      if (langName) currentFlag.setAttribute('alt', langName);
    }
    if (currentName && langName) currentName.textContent = langName;

    var code = option.getAttribute('data-locale-code');
    var options = Array.prototype.slice.call(root.querySelectorAll('[data-lang-selector-option]'));
    options.forEach(function (o) {
      var isMatch = code ? o.getAttribute('data-locale-code') === code : o === option;
      o.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });
  }

  /* Keeps every instance on the page (header + footer both render
     snippets/header-language-selector.liquid) showing the same flag/
     name/selected-state the instant one of them changes - same
     underlying Shopify localization state either way (see the comment
     at the top of this file), this only makes the OTHER already-mounted
     instance reflect it immediately instead of waiting for a full page
     reload. No separate language state is introduced: this just mirrors
     whichever option was just picked onto every other root by matching
     locale code. */
  function syncAllInstances(clickedRoot, option) {
    var code = option.getAttribute('data-locale-code');
    if (!code) return;
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-header-lang-selector]'));
    roots.forEach(function (root) {
      if (root === clickedRoot) return;
      var match = root.querySelector('[data-lang-selector-option][data-locale-code="' + code + '"]');
      if (match) applyFlag(root, match);
    });
  }

  function initLangSelector(root) {
    if (root.dataset.langSelectorInitialized === 'true') return;
    root.dataset.langSelectorInitialized = 'true';

    var trigger = root.querySelector('[data-lang-selector-trigger]');
    var dropdown = root.querySelector('[data-lang-selector-dropdown]');
    var select = root.querySelector('[data-lang-selector-select]');
    if (!trigger || !dropdown) return;

    var options = Array.prototype.slice.call(root.querySelectorAll('[data-lang-selector-option]'));

    var storedCode = readStoredLocale();
    if (storedCode) {
      var storedOption = options.find(function (o) {
        return o.getAttribute('data-locale-code') === storedCode;
      });
      if (storedOption) applyFlag(root, storedOption);
    }

    trigger.addEventListener('click', function (event) {
      event.stopPropagation();
      if (dropdown.hidden) {
        openDropdown(root);
      } else {
        closeDropdown(root);
      }
    });

    document.addEventListener('click', function (event) {
      if (!dropdown.hidden && !root.contains(event.target)) {
        closeDropdown(root);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !dropdown.hidden) {
        closeDropdown(root);
        trigger.focus();
      }
    });

    options.forEach(function (option) {
      option.addEventListener('click', function () {
        var code = option.getAttribute('data-locale-code');
        var available = option.getAttribute('data-locale-available') === 'true';

        applyFlag(root, option);
        syncAllInstances(root, option);
        if (code) writeStoredLocale(code);
        closeDropdown(root);

        if (available && select && code) {
          select.value = code;
          if (select.form) select.form.submit();
        }
      });
    });
  }

  function initAll(scope) {
    var context = scope || document;
    var roots = Array.prototype.slice.call(context.querySelectorAll('[data-header-lang-selector]'));
    roots.forEach(initLangSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  /**
   * The predictive-search dropdown (sections/predictive-search.liquid)
   * is fetched and rendered independently via Shopify's Section
   * Rendering API, so it has no access to the header section's own
   * settings - only inherited CSS custom properties cross that boundary
   * (see the comment in that file). Configurable WORDING ("Suggestions"
   * heading text, "See all"/"View all" link text) and the "number of
   * products shown" setting are bridged here instead: read once from
   * data attributes already rendered server-side on the header's own
   * .live-search element (which does have the header's settings, via
   * snippets/live-search-form.liquid), then applied to the freshly
   * fetched markup every time LiveSearch (assets/empire.js) injects a
   * new response into [data-live-search-results]. This never touches
   * Shopify's own fetch/search logic - it only edits text nodes and
   * trims the already-fetched product list down to the configured
   * count, after the real search data has arrived.
   */
  function applyPredictiveSearchOverrides(resultsEl) {
    var liveSearch = resultsEl.closest('[data-live-search]');
    if (!liveSearch) return;

    var suggestionsTitle = liveSearch.getAttribute('data-predictive-suggestions-title');
    var resultsTitle = liveSearch.getAttribute('data-predictive-results-title');
    var seeallText = liveSearch.getAttribute('data-predictive-seeall-text');
    var viewallText = liveSearch.getAttribute('data-predictive-viewall-text');
    var productCount = parseInt(liveSearch.getAttribute('data-predictive-product-count'), 10);

    var suggestionsHeading = resultsEl.querySelector('[data-predictive-suggestions-heading]');
    if (suggestionsHeading && suggestionsTitle) suggestionsHeading.textContent = suggestionsTitle;

    var resultsHeading = resultsEl.querySelector('[data-predictive-results-heading]');
    if (resultsHeading && resultsTitle) resultsHeading.textContent = resultsTitle;

    var seeall = resultsEl.querySelector('[data-predictive-seeall]');
    if (seeall && seeallText) seeall.textContent = seeallText;

    var viewall = resultsEl.querySelector('[data-predictive-viewall]');
    if (viewall && viewallText) viewall.textContent = viewallText;

    if (!isNaN(productCount) && productCount > 0) {
      var items = Array.prototype.slice.call(
        resultsEl.querySelectorAll('[data-predictive-products-list] > li')
      );
      items.slice(productCount).forEach(function (item) {
        item.remove();
      });
    }
  }

  function initPredictiveSearchOverrides(scope) {
    var context = scope || document;
    var resultsElements = Array.prototype.slice.call(
      context.querySelectorAll('[data-live-search-results]')
    );
    resultsElements.forEach(function (resultsEl) {
      if (resultsEl.dataset.predictiveOverridesInitialized === 'true') return;
      resultsEl.dataset.predictiveOverridesInitialized = 'true';

      applyPredictiveSearchOverrides(resultsEl);

      var observer = new MutationObserver(function () {
        applyPredictiveSearchOverrides(resultsEl);
      });
      observer.observe(resultsEl, { childList: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPredictiveSearchOverrides();
    });
  } else {
    initPredictiveSearchOverrides();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initPredictiveSearchOverrides(event.target);
  });

  /**
   * Bug: the mobile search bar (assets/empire.js's LiveSearch class)
   * closes itself ~2-3 seconds after the shopper starts typing, with
   * no user action involved. Root-caused via live instrumentation
   * (headless Chrome, capturing every document-level focusin/click/
   * touchstart while reproducing): LiveSearch registers a `document`
   * click/touchstart/focusin listener (`_documentFocus`, empire.js)
   * that closes the flydown/takeover the moment ANY such event's
   * target isn't inside [data-live-search] - a real "click away
   * closes search" pattern. The GemPages announcement/utility bar
   * above the header (sections/section-utility-bar.liquid, scoped by
   * its own .gps-634583750771999507 class) runs an autoplay carousel
   * that calls .click() on its own "next slide" button on a timer -
   * confirmed live, a real click event fires on that button around
   * every ~2.5s with no user interaction. That click bubbles to
   * document, LiveSearch's listener sees a target outside
   * [data-live-search], and closes search - a false positive from a
   * totally unrelated component, not anything the search widget
   * itself is timing out on.
   *
   * Fix: a document-level listener registered at page load (so it
   * runs before LiveSearch's own listener, which is only added
   * reactively once the shopper opens search) that stops the event
   * from propagating any further whenever it originates from inside
   * the utility bar - this only ever suppresses events that would've
   * gone on to hit LiveSearch's later `document` listener; it does
   * NOT touch the utility bar's own button listener (already fired,
   * earlier in the same bubble phase, before reaching document) or
   * any other functionality search/utility-bar rely on. No timer,
   * delay, or auto-hide logic is added or removed here - this purely
   * stops a false "click outside search" signal at its source.
   */
  ['click', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (event.target.closest('.gps-634583750771999507')) {
          event.stopImmediatePropagation();
        }
      },
      false
    );
  });
})();
